import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';

interface IProps {
  phoneNumber: string;
  onClose: () => void;
  isOpen: boolean;
  onVerify?: (otp: string) => void;
}

const TIMEOUT = 60; // 60 seconds for OTP expiration

function maskPhoneNumber(input: string): string {
  const [prefix, number] = input.split('-');
  const maskedPrefix = '*'.repeat(prefix?.length);
  const maskedNumber = '*'.repeat(number?.length - 2) + number?.slice(-2);
  return `${maskedPrefix}-${maskedNumber}`;
}

const PhoneVerificationModal: React.FC<IProps> = ({
  phoneNumber,
  onClose,
  isOpen,
  onVerify
}) => {
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(TIMEOUT);
  const [errorMessage, setErrorMessage] = useState('');
  const [prefix, number] = phoneNumber.split('-');

  // timer = 0 , set error message
  useEffect(() => {
    if (timer === 0) {
      setErrorMessage('OTP has expired. Please request a new one.');
    } else {
      setErrorMessage('');
    }
  }, [timer]);

  const requestOtp = useMutation({
    mutationFn: async ({ number, prefix }: { number: string, prefix: string }) => {
      await apiClient.post('/auth/request-otp', {
        phoneNumber: number,
        countryCode: prefix,
        eventType: 'phone_verify'
      });
    },
    onSuccess: () => {
      setTimer(TIMEOUT);
    },
    onError: (error) => {
      console.error('Error requesting OTP:', error);
      setErrorMessage((error as any)?.response?.data?.error?.message || 'Failed to request OTP. Please try again.');
      setTimer(0);
    }
  });

  useEffect(() => {
    let interval: any;
    if (isOpen) {
      requestOtp.mutate({ number, prefix });
      interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isOpen]);

  const handleVerify = () => {
    onVerify?.(otp);
    setOtp('');
  };

  const handleResend = () => {
    if (timer === 0) {
      requestOtp.mutate({ number, prefix });
    }
  };

  const handleCloseDialog = () => {
    onClose();
    setOtp('');
  };

  const handleChangePhoneNumber = () => {
    // Logic to change the phone number goes here
    console.log('Change phone number clicked')
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseDialog}>
      <DialogContent
        className="max-w-[24rem] rounded-3xl"
        aria-describedby='phone-verification-modal'
      >
        <DialogHeader className='mt-10'>
          <DialogTitle className='text-center'>Mobile Phone Verification</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col items-center'>
          <p className="text-md text-center my-2">One Time Password</p>
          {/* Add verification code input here */}
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={setOtp}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={1} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={4} />
            </InputOTPGroup>
            <InputOTPGroup>
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>


        <p className={cn("text-xs text-center mt-4 ", errorMessage ? "text-[#FF5E18]" : "text-[#aac4b9]")}>
          {
            errorMessage
            || `A OTP (One Time Passcode) has been sent to ${maskPhoneNumber(phoneNumber)}. Please enter the OTP in the field above to verify your phone.`
          }
        </p>

        {timer > 0 && (
          <p className='text-center text-sm mt-2'>
            Resend OTP in <strong>{timer}s</strong>
          </p>
        )}

        {timer > 0 ? (
          <Button
            className='bg-[#5CB891] hover:bg-[#5CB891] mt-3 rounded-full'
            onClick={() => handleVerify()}
            disabled={otp.length !== 6}
            type='button'
          >
            Verify
          </Button>
        ) : (
          <Button
            variant={'outline'}
              className='bg-white hover:bg-white mt-3 rounded-full border-[#5CB891] border-2
              text-[#5CB891] hover:text-[#5CB891] font-semibold text-md'
            onClick={() => handleResend()}
            type='button'
          >
            Resend OTP
          </Button>
        )}

        {timer === 0 && (
          <Button variant={'link'} className='decoration-[#5B5959]' onClick={handleChangePhoneNumber}>
            <p className='text-[#5B5959]'>
              Can't access old phone number?
            </p>
          </Button>
        )}


      </DialogContent>
    </Dialog>
  )
}

export default PhoneVerificationModal;